import { Routes, Route, Navigate, NavLink, useNavigate } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import { Button } from "react-bootstrap"
import {
  FiHome,
  FiLogOut,
  FiShield,
  FiCheckSquare,
  FiList,
  FiDroplet,
  FiPackage,
  FiBook,
  FiAlertCircle,
  FiMessageSquare,
  FiSliders,
  FiClipboard,
} from "react-icons/fi"

import "./index.scss"
import useT from "utils/hooks/useT"
import { HelpButton } from "components"
import { auth } from "state_management"
import { selectCanAdministerCapabilities } from "state_management/lims/limsCapabilities"
import LimsPlaceholder from "./LimsPlaceholder"
import AuditViewer from "./AuditViewer"
import ConsumablesRegister from "./ConsumablesRegister"
import ProductTestPlan from "./ProductTestPlan"
import QaSampleReview from "./QaSampleReview"
import QaBatchHolds from "./QaBatchHolds"
import Investigations from "./Investigations"
import AnalysisDocumentsRegister from "./AnalysisDocumentsRegister"
import CommentLibrary from "./CommentLibrary"
import SstTemplates from "./SstTemplates"
import WorklistTemplatesRegister from "./WorklistTemplatesRegister"

// The LIMS workspace sections. The section content plugs in exactly here; any section without a
// Component falls back to a "coming soon" placeholder.
//
// SPEC-011 removed the standalone Review queue (review now happens inline on the analysis task,
// surfaced via the right-panel Reviews inbox) and the OOS register screen. The limsOos slice was
// subsequently REVIVED by SPEC-018 (PRD-005 Area 4) as the structured two-phase OOS INVESTIGATION
// (opened from a FAIL result via the investigation modal, no standalone menu section).
// SPEC-012 replaced the OOS-keyed "QA decisions" screen with the "QA Analysis Review" worklist,
// which keys off results sent to QA (state "sent-to-qa") and decides per sample.
// SPEC-013 (A3) reworked the "QA Release" queue into "QA Batch Release" (the lot is releasable once
// all its analyses are QA-approved) and REMOVED the standalone CoA register — the batch certificate
// is reachable from the Sample View and from the QA Batch Release "Released" table.
// SPEC-022 (K10) added a standalone "Investigations" menu item: the team's entry to OPEN OOS
// investigations (Phase 1 is lab work). QA Analysis Review now shows only the subset QA must decide.
// SPEC-038 (F6) RETIRED the per-analysis "QA Analysis Review" worklist and replaced it with the
// sample/lot-level "QA SampleReview" (QaSampleReview): QA's unit is now the samplenr/lotnr, listed
// once it is COMPLETE across all its analysis runs, with Approve/Reject + the QA e-signature here.
// The old qa-analysis-review path redirects below for bookmarks; per-analysis QA decisions still
// live inline on the analysis (QaDecisionSection in "QC Results & Approvals").
// `requiresAdmin` gates a section to capability-admins (planner / qa-manager): the comment-library
// is stamdata maintenance, so non-admins neither see it in the sidebar nor can route to it (the
// backend POST/PATCH are 403 regardless). All other sections are open to any LIMS user.
// R8 (#4087): the order is the ORDER OF WORK, not the order the sections were built in. Daily QA
// queues first (sample review, then the holds that block it, then investigations), then the
// stamdata registers a QA-admin maintains (consumables, SST templates, worklist templates, analysis
// documents, comment library), then the read-only overviews (product readiness, audit trail).
// R9: `qa-batch-release` is labelled QA Batch Holds — the screen is holds-only from R10 on. Its
// `path` is deliberately unchanged, along with all four redirects below, so bookmarks survive (R12).
const SECTIONS = [
  { path: "qa-sample-review", labelKey: "limsQaSampleReview", Icon: FiCheckSquare, Component: QaSampleReview },
  { path: "qa-batch-release", labelKey: "limsQaBatchHolds", Icon: FiShield, Component: QaBatchHolds },
  { path: "investigations", labelKey: "limsInvestigations", Icon: FiAlertCircle, Component: Investigations },
  { path: "consumables", labelKey: "limsConsumables", Icon: FiDroplet, Component: ConsumablesRegister },
  { path: "sst-templates", labelKey: "limsSstTemplatesTitle", Icon: FiSliders, Component: SstTemplates, requiresAdmin: true },
  { path: "worklist-templates", labelKey: "limsWorklistTemplatesTitle", Icon: FiClipboard, Component: WorklistTemplatesRegister, requiresAdmin: true },
  { path: "analysis-documents", labelKey: "limsForskrifter", Icon: FiBook, Component: AnalysisDocumentsRegister },
  { path: "comment-library", labelKey: "limsCommentLibrary", Icon: FiMessageSquare, Component: CommentLibrary, requiresAdmin: true },
  { path: "products", labelKey: "limsProductReadiness", Icon: FiPackage, Component: ProductTestPlan },
  { path: "audit", labelKey: "limsAudit", Icon: FiList, Component: AuditViewer },
]

const DEFAULT_SECTION = "/lims/products"

// Dedicated LIMS workspace. Mounted at /lims/* (gated by showLims in App.jsx) and owns
// its own sub-navigation, mirroring how LibrarianScreen owns its layout + nested Routes.
export default function LimsScreen() {
  const canAdminister = useSelector(selectCanAdministerCapabilities)
  // Admin-only sections drop out of both the routes and the sidebar for non-admins (the route is
  // simply not registered, so a deep link falls through to the catch-all redirect).
  const visibleSections = SECTIONS.filter((section) => !section.requiresAdmin || canAdminister)
  return (
    <div className="LimsScreen">
      <LimsSidebar sections={visibleSections} />
      <div className="LimsScreen-main">
        <LimsToolbar />
        <div className="LimsScreen-content">
          <Routes>
            <Route path="/" element={<Navigate to={DEFAULT_SECTION} replace />} />
            {/* The reagents register was generalised to the consumables register (PRD-003
                Area 3); keep the old path working for existing bookmarks / deep links. */}
            <Route path="reagents" element={<Navigate to="/lims/consumables" replace />} />
            {/* "Method documents" (forskrifter) was renamed to "Analysis documents" and inverted
                to be analysis-first (SPEC-014); keep the old path working for bookmarks. */}
            <Route path="forskrifter" element={<Navigate to="/lims/analysis-documents" replace />} />
            {/* The OOS-keyed "QA decisions" screen → result-keyed "QA Analysis Review" (SPEC-012) →
                sample/lot-level "QA SampleReview" (SPEC-038). Old paths redirect for bookmarks. */}
            <Route path="qa-decisions" element={<Navigate to="/lims/qa-sample-review" replace />} />
            <Route path="qa-analysis-review" element={<Navigate to="/lims/qa-sample-review" replace />} />
            {/* "QA Release" was reworked into "QA Batch Release" (SPEC-013); keep the old path. */}
            <Route path="qa-release" element={<Navigate to="/lims/qa-batch-release" replace />} />
            {/* The standalone CoA register was removed (SPEC-013). R10 also removed the "Released"
                table this used to land beside, so the batch certificate now lives ONLY on the Sample
                View and QA Sample Review. The redirect is kept as-is for bookmarks (R12) — it resolves
                to a live screen, just no longer to a certificate. */}
            <Route path="coa" element={<Navigate to="/lims/qa-batch-release" replace />} />
            {visibleSections.map((section) => (
              <Route
                key={section.path}
                path={section.path}
                element={
                  section.Component ? <section.Component /> : <LimsPlaceholder titleKey={section.labelKey} />
                }
              />
            ))}
            <Route path="*" element={<Navigate to={DEFAULT_SECTION} replace />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}

function LimsSidebar({ sections }) {
  const { t } = useT("common")
  return (
    <nav className="LimsScreen-sidebar">
      <div className="LimsScreen-sidebar-title">{t("lims")}</div>
      {sections.map(({ path, labelKey, Icon }) => (
        <NavLink
          key={path}
          to={`/lims/${path}`}
          className={({ isActive }) => "LimsScreen-navitem" + (isActive ? " active" : "")}
        >
          <Icon />
          <span>{t(labelKey)}</span>
        </NavLink>
      ))}
    </nav>
  )
}

// Top toolbar mirroring the Librarian toolbar: Home centered, Help + Exit on the right.
// Home, Help and Exit reuse the app's existing navigation/logout functions.
function LimsToolbar() {
  const { t } = useT("common")
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isMember = useSelector((state) => state.auth.isMember)
  const memberId = useSelector((state) => state.auth.memberId)
  const directToMemberPlan = useSelector((state) => state.teamStatus.directToMemberPlan)
  const gmpRelaxed = useSelector((state) => state.auth.gmpControlsRelaxed) // SPEC-025: dev-only badge

  const handleLogout = () => {
    dispatch(auth.logout()).then(() => navigate("/login"))
  }

  return (
    <div className="LimsScreen-toolbar">
      {gmpRelaxed && (
        <div className="LimsScreen-toolbar-devbadge">
          <FiAlertCircle />
          <span>{t("limsGmpRelaxedBadge")}</span>
        </div>
      )}
      <div className="LimsScreen-toolbar-nav">
        <Button
          variant="primary"
          onClick={() => navigate(isMember && directToMemberPlan ? `/member/${memberId}` : "/")}
        >
          <FiHome title={t("home")} />
        </Button>
      </div>
      <div className="LimsScreen-toolbar-buttons">
        <HelpButton area="lims" />
        <Button variant="primary" onClick={handleLogout} title={t("logout")}>
          <FiLogOut />
        </Button>
      </div>
    </div>
  )
}
